import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useI18n, formatPrice } from "../i18n";
import { fetchCars } from "../api";
import { ArrowRight, RotateCcw, Calendar, Sparkles } from "lucide-react";

const Monthly = () => {
  const { lang } = useI18n();
  const isPt = lang === "pt";
  const [cars, setCars] = useState([]);
  const [plan, setPlan] = useState("monthly"); // monthly | home

  useEffect(() => {
    fetchCars().then(setCars).catch(() => setCars([]));
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20" data-testid="monthly-page">
      <p className="text-label text-[#FFB400]">
        <span className="line-accent" />
        {isPt ? "Aluguel mensal · Movix em Casa" : "Monthly rental · Movix at Home"}
      </p>
      <h1 className="mt-4 font-heading text-5xl sm:text-6xl font-bold tracking-tighter text-white max-w-4xl">
        {isPt ? (
          <>Um carro fixo, <span className="text-[#FFB400]">sem fiador, sem entrada.</span></>
        ) : (
          <>A car of your own, <span className="text-[#FFB400]">no co-signer, no down payment.</span></>
        )}
      </h1>
      <p className="mt-6 max-w-2xl text-zinc-300 text-lg leading-relaxed">
        {isPt
          ? "Para quem chegou em Boston pra ficar — estudante, motorista de app, recém-imigrante, profissional aguardando green card. Mensal fixo, seguro incluso, manutenção zero."
          : "For people who came to Boston to stay — student, rideshare driver, new immigrant, professional waiting on green card. Flat monthly, insurance included, zero maintenance hassle."}
      </p>

      {/* Plan toggle */}
      <div
        className="mt-12 inline-flex border border-white/10"
        data-testid="monthly-plan-toggle"
      >
        <button
          onClick={() => setPlan("monthly")}
          className={`px-5 py-3 text-xs font-mono-ui uppercase tracking-widest transition-colors ${
            plan === "monthly" ? "bg-[#FFB400] text-[#0a0a0a]" : "text-zinc-400 hover:text-white"
          }`}
          data-testid="plan-monthly-btn"
        >
          <Calendar size={12} className="inline mr-2 -mt-0.5" />
          {isPt ? "Mensal fixo" : "Monthly flat"}
        </button>
        <button
          onClick={() => setPlan("home")}
          className={`px-5 py-3 text-xs font-mono-ui uppercase tracking-widest transition-colors ${
            plan === "home" ? "bg-[#FFB400] text-[#0a0a0a]" : "text-zinc-400 hover:text-white"
          }`}
          data-testid="plan-home-btn"
        >
          <RotateCcw size={12} className="inline mr-2 -mt-0.5" />
          {isPt ? "Movix em Casa (troca a cada 3 meses)" : "Movix at Home (swap every 3 months)"}
        </button>
      </div>

      {/* Plan summary */}
      <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10">
        {(plan === "monthly"
          ? [
              { t: isPt ? "Prazo mínimo" : "Min. term", v: isPt ? "1 mês" : "1 month" },
              { t: isPt ? "Desconto vs diária" : "Discount vs daily", v: "40%" },
              { t: isPt ? "Inclui" : "Includes", v: isPt ? "Seguro + KM livre" : "Insurance + unlimited miles" },
            ]
          : [
              { t: isPt ? "Prazo mínimo" : "Min. term", v: isPt ? "3 meses" : "3 months" },
              { t: isPt ? "Troca de carro" : "Car swap", v: isPt ? "A cada 90 dias" : "Every 90 days" },
              { t: isPt ? "Desconto vs diária" : "Discount vs daily", v: "45%" },
            ]
        ).map((s, i) => (
          <div key={i} className="bg-[#0a0a0a] p-6">
            <p className="text-label">{s.t}</p>
            <p className="mt-3 font-heading text-2xl font-bold text-white">{s.v}</p>
          </div>
        ))}
      </div>

      {/* Cars with monthly pricing */}
      <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {cars.map((c) => {
          const monthlyPrice = plan === "home" ? c.movix_home_price : c.monthly_price;
          return (
            <div
              key={c.id}
              className="bg-[#0d0d0d] border border-white/10 overflow-hidden lift"
              data-testid={`monthly-car-${c.id}`}
            >
              <div className="aspect-[16/10] bg-[#0a0a0a]">
                <img src={c.image} alt={c.name} className="w-full h-full object-cover" />
              </div>
              <div className="p-5">
                <p className="text-label">{c.brand}</p>
                <h3 className="font-heading text-xl text-white tracking-tight">{c.name}</h3>
                <p className="text-xs text-zinc-500 mt-1 font-mono-ui uppercase tracking-widest">
                  {c.category} · {c.year}
                </p>

                <div className="mt-5 pt-4 border-t border-white/5 flex items-baseline justify-between">
                  <div>
                    <p className="text-label">{isPt ? "Mensal" : "Monthly"}</p>
                    <p className="font-heading text-3xl font-bold text-[#FFB400]">
                      {formatPrice(monthlyPrice)}
                    </p>
                    <p className="text-xs text-zinc-500">
                      {isPt ? "vs diária" : "vs daily"}: {formatPrice(c.price_per_day * 30)}
                    </p>
                  </div>
                  <Link
                    to="/contact"
                    className="text-[#FFB400] hover:underline text-xs font-mono-ui uppercase tracking-widest"
                    data-testid={`monthly-cta-${c.id}`}
                  >
                    {isPt ? "Reservar" : "Reserve"} →
                  </Link>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* CTA */}
      <div className="mt-20 bg-[#0d0d0d] border border-[#FFB400]/40 p-10 md:p-14 grid grid-cols-1 md:grid-cols-3 gap-8 items-center" data-testid="monthly-cta">
        <div className="md:col-span-2">
          <Sparkles size={28} className="text-[#FFB400]" />
          <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-semibold text-white tracking-tight">
            {isPt
              ? "Recém-chegou em Boston? Primeira mensalidade com 30% off."
              : "Just arrived in Boston? First month 30% off."}
          </h2>
          <p className="mt-4 text-zinc-300 leading-relaxed max-w-xl">
            {isPt
              ? "Programa Recém-chegado: aceita CNH brasileira, sem cossignatário, sem entrada. Comprovante de chegada nos últimos 6 meses (carimbo no passaporte ou contrato de aluguel)."
              : "Newcomer program: Brazilian license accepted, no co-signer, no down payment. Proof of US arrival in the last 6 months (passport stamp or rental lease)."}
          </p>
        </div>
        <Link to="/welcome" className="btn-primary glow-coral justify-center" data-testid="monthly-welcome-cta">
          {isPt ? "Sou recém-chegado" : "I'm a newcomer"} <ArrowRight size={16} />
        </Link>
      </div>
    </div>
  );
};

export default Monthly;
